import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatQty } from "@/lib/materials";
import { getPhotoSpanClass } from "@/lib/photoLayout";
import { printWithTitle } from "@/lib/printWithTitle";
import Logo from "@/components/Logo";
import {
  CheckCircle2, AlertTriangle, Building2, Calendar, MapPin, User, Printer,
  MessageCircle, Package, X, ChevronLeft, ChevronRight, ArrowLeft,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Read-only lightbox for public preview (foreman/customer). Allows opening photos fullscreen and reading caption.
function PublicPhotoLightbox({ photos, startIndex = 0, onClose }) {
  const [idx, setIdx] = useState(startIndex);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") setIdx((i) => Math.max(0, i - 1));
      if (e.key === "ArrowRight") setIdx((i) => Math.min(photos.length - 1, i + 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [photos.length, onClose]);
  if (!photos.length) return null;
  const photo = photos[idx];
  return (
    <div className="fixed inset-0 z-50 bg-black/85 flex flex-col print-hide" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="public-lightbox">
      <div className="flex items-center justify-between px-4 md:px-6 h-14 text-white">
        <div className="text-sm font-semibold">{idx + 1} / {photos.length}</div>
        <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10" data-testid="public-lightbox-close">
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="flex-1 flex items-center justify-center px-2 md:px-12 relative min-h-0">
        {idx > 0 && (
          <button onClick={() => setIdx((i) => i - 1)} className="absolute left-2 md:left-6 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white">
            <ChevronLeft className="h-6 w-6" />
          </button>
        )}
        <img src={`${BACKEND}/api/photos/${photo.photo_id}`} alt={photo.caption || ""} className="max-h-full max-w-full object-contain rounded-lg" />
        {idx < photos.length - 1 && (
          <button onClick={() => setIdx((i) => i + 1)} className="absolute right-2 md:right-6 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white">
            <ChevronRight className="h-6 w-6" />
          </button>
        )}
      </div>
      {photo.caption && (
        <div className="bg-black/70 px-4 py-4 text-center text-white text-sm max-w-3xl mx-auto w-full">
          {photo.caption}
        </div>
      )}
    </div>
  );
}

function MarkdownLight({ text }) {
  const lines = (text || "").split("\n");
  return (
    <div data-testid="markdown-light">
      {lines.map((line, i) => {
        if (line.startsWith("### ")) return <h3 key={i}>{line.replace("### ", "")}</h3>;
        if (line.startsWith("## ")) return <h3 key={i}>{line.replace("## ", "")}</h3>;
        if (line.startsWith("- ")) return <li key={i} style={{ marginLeft: "1.25rem" }}>{line.replace("- ", "")}</li>;
        if (!line.trim()) return <div key={i} style={{ height: 8 }} />;
        return <p key={i}>{line}</p>;
      })}
    </div>
  );
}

export default function PublicReport() {
  const { shareToken } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const autoPrint = params.get("print") === "1";
  const [report, setReport] = useState(null);
  const [err, setErr] = useState(false);
  const [lightbox, setLightbox] = useState(null); // { photos, startIndex }
  const [preparing, setPreparing] = useState(null); // { loaded, total } or null
  // Muokkaustilasta avattu esikatselu tallentaa "veraso_preview_back":n
  // sessionStorageen — näytämme silloin selkeän paluulinkin niin että
  // käyttäjä (etenkin iOS-Safari/PWA) pääsee pois esikatselusta.
  const [previewBack, setPreviewBack] = useState(null);
  useEffect(() => {
    try {
      const back = window.sessionStorage.getItem("veraso_preview_back");
      if (back) setPreviewBack(back);
    } catch { /* ignore */ }
  }, []);
  const exitPreview = () => {
    try { window.sessionStorage.removeItem("veraso_preview_back"); } catch { /* ignore */ }
    if (previewBack) navigate(previewBack);
    else navigate(-1);
  };

  useEffect(() => {
    api
      .get(`/reports/public/${shareToken}`)
      .then(({ data }) => setReport(data))
      .catch(() => setErr(true));
  }, [shareToken]);

  // Preload all photo thumbnails into the browser cache and wait for them all
  // to decode before opening the print dialog. This prevents the print preview
  // from rendering with half-loaded images and gives the user clear progress
  // feedback for long reports (50+ units, 100+ photos).
  const preloadThumbsAndPrint = async () => {
    if (!report) return;
    const photoIds = [];
    (report.units || []).forEach((u) => (u.photos || []).forEach((p) => p.photo_id && photoIds.push(p.photo_id)));
    const total = photoIds.length;

    if (total === 0) {
      const label = (report.title || report.work_type || "").trim();
      printWithTitle(label ? `Raportti - ${label}` : "Raportti");
      return;
    }

    setPreparing({ loaded: 0, total });
    let loaded = 0;
    const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

    // Decode in parallel. Browsers cap concurrent HTTP/2 requests to ~6 per
    // origin so this won't blow up the connection budget. We use Image() so
    // the result lands in the same HTTP cache the printed <img> will use.
    await Promise.all(
      photoIds.map((id) =>
        new Promise((resolve) => {
          const img = new Image();
          const done = () => { loaded += 1; setPreparing({ loaded, total }); resolve(); };
          img.onload = done;
          img.onerror = done; // don't block PDF on a single broken image
          img.src = `${apiBase}/api/photos/${id}/thumb`;
        })
      )
    );

    // One animation frame so React paints the final "100%" state before the
    // (synchronous) print dialog steals the main thread.
    await new Promise((r) => requestAnimationFrame(r));
    setPreparing(null);
    const label = (report.title || report.work_type || "").trim();
    await printWithTitle(label ? `Raportti - ${label}` : "Raportti");
  };

  useEffect(() => {
    if (autoPrint && report) {
      const t = setTimeout(() => preloadThumbsAndPrint(), 400);
      return () => clearTimeout(t);
    }
  }, [autoPrint, report]);

  // Dynamic browser/PDF document title — used by browsers as PDF filename hint and tab title.
  useEffect(() => {
    if (!report) return;
    const label = (report.title || report.work_type || "").trim();
    const prev = document.title;
    document.title = label ? `Raportti - ${label}` : "Raportti";
    return () => { document.title = prev; };
  }, [report]);

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="font-display text-xl text-slate-900">Raporttia ei löytynyt</div>
          <p className="text-slate-500 mt-2">Linkki on vanhentunut tai virheellinen.</p>
        </div>
      </div>
    );
  }
  if (!report) return <div className="min-h-screen flex items-center justify-center text-slate-500">Ladataan...</div>;

  const findingsByUnit = (u) =>
    (u.checklist || []).filter((c) => c.checked && c.is_finding).map((c) => c.label);
  const extras = report.units.filter(
    (u) => (u.additional_work_text || "").trim() || findingsByUnit(u).length > 0,
  );
  const stats = {
    total: report.units.length,
    done: report.units.filter((u) => u.status === "done").length,
    extra: report.units.filter((u) => (u.additional_work_text || "").trim() || findingsByUnit(u).length > 0).length,
  };

  // Report creator (author) — prefer the actual person who created the report.
  // Falls back to a unit-level executor if not available (legacy reports).
  const creatorName = (report.created_by_name || "").trim();
  const executorPerson = creatorName || (report.units.find((u) => (u.executed_by || "").trim()) || {}).executed_by || "";
  const executorCompany = report.executor_company || report._company?.name || "";
  const formattedDate = new Date(report.report_date || report.created_at).toLocaleDateString("fi-FI");

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto p-3 md:p-8 print-page">
        <div className="print-hide flex items-center justify-between gap-3 mb-3">
          {previewBack ? (
            <button
              onClick={exitPreview}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-sm font-semibold text-slate-700 whitespace-nowrap shadow-sm"
              data-testid="preview-back-button"
            >
              <ArrowLeft className="h-4 w-4" /> Takaisin muokkaukseen
            </button>
          ) : (
            <span />
          )}
          <button
            onClick={preloadThumbsAndPrint}
            disabled={!!preparing}
            className="btn-primary text-sm disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="public-print-button"
          >
            <Printer className="h-4 w-4" /> Lataa PDF / Tulosta
          </button>
        </div>

        {/* PDF preparation progress overlay */}
        {preparing && (
          <div className="print-hide fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm" data-testid="pdf-preparing-overlay">
            <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 max-w-sm w-[90%]" data-testid="pdf-preparing-card">
              <div className="font-display text-lg font-semibold text-slate-900">Valmistellaan PDF:ää…</div>
              <p className="text-sm text-slate-600 mt-1">
                Ladataan kuvat tulostusta varten. Tämä kestää sitä kauemmin, mitä enemmän kuvia raportissa on.
              </p>
              <div className="mt-5 h-2 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className="h-full bg-emerald-500 transition-all duration-200"
                  style={{ width: `${Math.round((preparing.loaded / Math.max(preparing.total, 1)) * 100)}%` }}
                  data-testid="pdf-progress-bar"
                />
              </div>
              <div className="mt-2 text-xs text-slate-500 text-right" data-testid="pdf-progress-count">
                {preparing.loaded} / {preparing.total} kuvaa
              </div>
            </div>
          </div>
        )}

        <div className="card-soft overflow-hidden report-print" data-testid="report-document">

          {/* === COVER PAGE === */}
          <section className="report-cover relative bg-white p-8 md:p-14" data-testid="report-cover">
            {/* Subtle decorative gradient */}
            <div className="absolute top-0 right-0 w-72 h-72 -mr-24 -mt-24 brand-gradient opacity-[0.07] blur-3xl rounded-full pointer-events-none" />

            <div className="relative">
              {/* Top: customer logo + name only. Veraso branding moved to footer. */}
              <div className="pb-8 border-b border-slate-100">
                {report._company?.logo && (
                  <img src={report._company.logo} alt={executorCompany} className="h-16 md:h-20 max-w-[260px] object-contain" data-testid="public-company-logo" />
                )}
                <div className={`${report._company?.logo ? "mt-2" : ""} font-display text-base md:text-lg font-semibold text-slate-900`}>{executorCompany}</div>
                {(report._company?.phone || report._company?.email) && (
                  <div className="text-xs text-slate-500 mt-0.5">
                    {[report._company?.phone, report._company?.email, report._company?.website].filter(Boolean).join(" · ")}
                  </div>
                )}
              </div>

              {/* Title block — poistettu "RAPORTTI"-eyebrow */}
              <div className="mt-10 md:mt-14">
                <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.05]">
                  {report.title}
                </h1>
                {report.site_address && (
                  <div className="mt-4 inline-flex items-center gap-1.5 text-slate-600 text-sm">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 inline-block" />
                    {report.site_address}
                  </div>
                )}
              </div>

              {/* Meta grid */}
              <div className="mt-10 grid grid-cols-2 md:grid-cols-3 print:grid-cols-3 gap-y-5 gap-x-8 text-sm pb-2">
                {[
                  { l: "Päivämäärä", v: formattedDate, icon: Calendar, show: true },
                  { l: "Kohteen tyyppi", v: report.property_type || "—", icon: Building2, show: true },
                  { l: "Työn suorittaja", v: executorCompany || "—", icon: Building2, show: report.ask_executor !== false },
                ].filter((m) => m.show).map((m, i) => (
                  <div key={i}>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold inline-flex items-center gap-1.5">
                      <m.icon className="h-3 w-3" /> {m.l}
                    </div>
                    <div className="text-slate-900 font-display font-semibold mt-0.5 truncate">{m.v}</div>
                  </div>
                ))}
                {report.customer_name && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold inline-flex items-center gap-1.5">
                      <User className="h-3 w-3" /> Asiakas
                    </div>
                    <div className="text-slate-900 font-display font-semibold mt-0.5 truncate">{report.customer_name}</div>
                  </div>
                )}
                {executorPerson && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold inline-flex items-center gap-1.5">
                      <User className="h-3 w-3" /> Raportin laatija
                    </div>
                    <div className="text-slate-900 font-display font-semibold mt-0.5 truncate">{executorPerson}</div>
                  </div>
                )}
              </div>

              {/* Contact details — visible only if any contact field present */}
              {report.contact && (report.contact.name || report.contact.phone || report.contact.email || report.contact.address || report.contact.notes) && (
                <div className="mt-8 rounded-2xl border border-slate-100 bg-slate-50/60 p-5" data-testid="public-report-contact">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Yhteystiedot</div>
                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm text-slate-800">
                    {report.contact.name && <div><span className="text-slate-500">Yhteyshenkilö:</span> <span className="font-medium">{report.contact.name}</span></div>}
                    {report.contact.phone && <div><span className="text-slate-500">Puhelin:</span> <a href={`tel:${report.contact.phone}`} className="font-medium hover:underline">{report.contact.phone}</a></div>}
                    {report.contact.email && <div><span className="text-slate-500">Sähköposti:</span> <a href={`mailto:${report.contact.email}`} className="font-medium hover:underline">{report.contact.email}</a></div>}
                    {report.contact.address && <div><span className="text-slate-500">Osoite:</span> <span className="font-medium">{report.contact.address}</span></div>}
                  </div>
                  {report.contact.notes && (
                    <div className="mt-3 text-sm text-slate-700 whitespace-pre-wrap">
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Lisätiedot</div>
                      {report.contact.notes}
                    </div>
                  )}
                </div>
              )}

              {/* Tehdyt toimenpiteet / lisätiedot — valkoinen, vasen reunakorostus */}
              {(report.ai_summary || "").trim() && (
                <div className="mt-10 rounded-lg border border-slate-200 border-l-[3px] border-l-emerald-500 bg-white p-5 break-inside-avoid" data-testid="public-summary">
                  <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                    <MessageCircle className="h-3 w-3 text-emerald-600" /> Tehdyt toimenpiteet / Lisätiedot
                  </div>
                  <div className="mt-2.5 text-[15px] text-slate-800 leading-relaxed report-prose">
                    <MarkdownLight text={report.ai_summary} />
                  </div>
                </div>
              )}

              {/* Havainnot — yhtenäinen listamuoto yksikkökohtaisesti */}
              {!report.hide_findings_section && extras.length > 0 && (
                <div className="mt-8 rounded-lg border border-slate-200 border-l-[3px] border-l-amber-500 bg-white p-5 break-inside-avoid" data-testid="public-findings-summary">
                  <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                    <AlertTriangle className="h-3 w-3 text-amber-600" /> Havainnot
                  </div>
                  <div className="mt-3 space-y-3">
                    {extras.map((u) => {
                      const fItems = findingsByUnit(u);
                      const extraLines = ((u.additional_work_text || "").trim() || "")
                        .split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
                      const bullets = [...fItems, ...extraLines];
                      if (bullets.length === 0) return null;
                      return (
                        <div key={u.id} className="text-[14px] text-slate-800" data-testid={`public-finding-${u.id}`}>
                          <div className="font-semibold text-slate-900">{u.label}</div>
                          <ul className="mt-1 text-slate-800 space-y-0.5">
                            {bullets.map((label, i) => (
                              <li key={i} className="flex items-baseline gap-2">
                                <span className="text-slate-400 leading-none">•</span>
                                <span className="flex-1">{label}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Tarvikkeet — valkoinen, leader-dot -tyyli yksikkökohtaisesti */}
              {!report.hide_materials && (() => {
                const perUnit = (report.units || [])
                  .map((u) => ({ id: u.id, label: u.label, items: u.materials_used || [] }))
                  .filter((x) => x.items.length > 0);
                if (perUnit.length === 0) return null;
                return (
                  <div className="mt-8 rounded-lg border border-slate-200 border-l-[3px] border-l-blue-500 bg-white p-5 break-inside-avoid" data-testid="public-materials-section">
                    <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                      <Package className="h-3 w-3 text-blue-600" /> Tarvikkeet
                    </div>
                    <div className="mt-3 space-y-3">
                      {perUnit.map((mu) => (
                        <div key={mu.id} className="text-[14px]" data-testid={`public-materials-unit-${mu.id}`}>
                          <div className="font-semibold text-slate-900">{mu.label}</div>
                          <dl className="mt-1 space-y-0.5">
                            {mu.items.map((m, i) => (
                              <div key={i} className="flex items-baseline gap-2 leader-dots">
                                <dt className="text-slate-800">{m.name}</dt>
                                <span className="flex-1 border-b border-dotted border-slate-300 translate-y-[-3px]" aria-hidden="true"></span>
                                <dd className="text-slate-700 whitespace-nowrap">{formatQty(m.qty)} {m.unit || "kpl"}</dd>
                              </div>
                            ))}
                          </dl>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          </section>

          {/* === DETAIL PAGES (units etc.) === */}
          <section className="report-body p-6 md:p-10 space-y-8 bg-white">
            <div>
              <div className="flex items-center justify-between mb-3 print-keep-with-next">
                <h2 className="font-display text-2xl font-semibold text-slate-900">Käsitellyt kohteet</h2>
                <div className="text-[11px] text-slate-500">Kohteita: {stats.total}</div>
              </div>
              <div className="space-y-4">
                {report.units.map((u) => {
                  // Uusi mobile-native laitelista (per-yksikkö). Legacy `device_info`
                  // (raporttitasolla määritetty schema) toimii fallbackina vanhoille
                  // raporteille.
                  const devices = u.devices || [];
                  const legacyDeviceEntries = (report.device_fields || [])
                    .map((f) => ({ label: f.label, value: (u.device_info || {})[f.id] }))
                    .filter((x) => (x.value || "").trim());
                  const measurementEntriesNew = u.measurement_entries || [];
                  const measurementEntries = (report.measurement_fields || [])
                    .map((f) => ({ label: f.label, unit: f.unit, value: (u.measurements || {})[f.id] }))
                    .filter((x) => (x.value || "").trim());
                  const workChecklistBadges = (u.checklist || [])
                    .filter((c) => c.checked && !c.is_finding && !report.hide_work_checklists);
                  const findings = findingsByUnit(u);
                  const hasFindings = ((u.additional_work_text || "").trim()) || findings.length > 0;
                  const hasDevices = devices.length > 0 || legacyDeviceEntries.length > 0;
                  return (
                  <article key={u.id} className="rounded-2xl border border-slate-100 p-5 md:p-7 bg-white print-unit-flow" data-testid={`public-unit-${u.id}`}>
                    <header className="flex items-start gap-3 print-keep-with-next">
                      {u.status === "done" ? (
                        <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                      ) : (
                        <div className="h-5 w-5 rounded-full border-2 border-slate-300 flex-shrink-0 mt-0.5" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-display text-xl font-semibold text-slate-900">{u.label}</div>
                        {u.executed_by && (
                          <div className="text-[13px] text-slate-500 mt-1">Suorittaja: <span className="font-medium text-slate-700">{u.executed_by}</span></div>
                        )}
                      </div>
                    </header>

                    {/* Laitetiedot — mobile-native (unit.devices) TAI legacy (device_fields) */}
                    {hasDevices && (
                      <section className="mt-8 print-keep-with-next" data-testid={`public-unit-device-${u.id}`}>
                        <h3 className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Laitetiedot</h3>
                        {devices.length > 0 ? (
                          (() => {
                            const anyLoc = devices.some((d) => (d.location || "").trim());
                            const cols = anyLoc ? "grid-cols-3" : "grid-cols-2";
                            return (
                              <dl className="text-[15px]">
                                <div className={`grid ${cols} gap-x-6 text-[11px] uppercase tracking-wider text-slate-400 font-semibold pb-1 border-b border-slate-200 mb-1`}>
                                  <span>Merkki ja malli</span>
                                  <span>Sarjanumero / Tunniste</span>
                                  {anyLoc && <span>Sijainti</span>}
                                </div>
                                {devices.map((d) => (
                                  <div key={d.id} className={`grid ${cols} gap-x-6 py-1 border-b border-dotted border-slate-200 last:border-b-0`}>
                                    <dt className="text-slate-900">{d.brand_model || <span className="text-slate-300">—</span>}</dt>
                                    <dd className="text-slate-700">{d.serial_id || <span className="text-slate-300">—</span>}</dd>
                                    {anyLoc && <dd className="text-slate-700">{d.location || <span className="text-slate-300">—</span>}</dd>}
                                  </div>
                                ))}
                              </dl>
                            );
                          })()
                        ) : (
                          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1 text-[15px]">
                            {legacyDeviceEntries.map((d, i) => (
                              <div key={i} className="flex items-baseline gap-2 border-b border-dotted border-slate-200 py-1">
                                <dt className="text-slate-500 min-w-[100px]">{d.label}</dt>
                                <dd className="text-slate-900 font-medium flex-1">{d.value}</dd>
                              </div>
                            ))}
                          </dl>
                        )}
                      </section>
                    )}

                    {/* Mittaustulokset — mobile-native (unit.measurement_entries) TAI legacy (measurement_fields) */}
                    {(measurementEntriesNew.length > 0 || measurementEntries.length > 0) && (
                      <section className="mt-8 print-keep-with-next" data-testid={`public-unit-measurements-${u.id}`}>
                        <h3 className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Mittaustulokset</h3>
                        {measurementEntriesNew.length > 0 ? (
                          <dl className="text-[15px]">
                            <div className="grid grid-cols-[1fr_auto_auto] gap-x-6 text-[11px] uppercase tracking-wider text-slate-400 font-semibold pb-1 border-b border-slate-200 mb-1">
                              <span>Mittaus</span>
                              <span className="text-right">Tulos</span>
                              <span>Yksikkö</span>
                            </div>
                            {measurementEntriesNew.map((m) => (
                              <div key={m.id} className="grid grid-cols-[1fr_auto_auto] gap-x-6 py-1 border-b border-dotted border-slate-200 last:border-b-0">
                                <dt className="text-slate-900">{m.name || <span className="text-slate-300">—</span>}</dt>
                                <dd className="text-slate-900 font-medium text-right">{m.value || <span className="text-slate-300">—</span>}</dd>
                                <dd className="text-slate-500 min-w-[40px]">{m.unit || ""}</dd>
                              </div>
                            ))}
                          </dl>
                        ) : (
                          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1 text-[15px]">
                            {measurementEntries.map((m, i) => (
                              <div key={i} className="flex items-baseline justify-between border-b border-dotted border-slate-200 py-1">
                                <dt className="text-slate-500">{m.label}</dt>
                                <dd className="text-slate-900 font-medium whitespace-nowrap ml-2">
                                  {m.value}{m.unit && <span className="ml-1 text-slate-500 font-normal text-[13px]">{m.unit}</span>}
                                </dd>
                              </div>
                            ))}
                          </dl>
                        )}
                      </section>
                    )}

                    {/* Muut kerättävät tiedot — vain jos raportilla on custom_fields ja jokin täytetty */}
                    {(() => {
                      const defs = report.custom_fields || [];
                      const vals = u.custom_values || {};
                      const filled = defs
                        .map((f) => ({ def: f, value: (vals[f.id] || "").toString().trim() }))
                        .filter((x) => x.value);
                      if (filled.length === 0) return null;
                      return (
                        <section className="mt-8 print-keep-with-next" data-testid={`public-unit-custom-${u.id}`}>
                          <h3 className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Muut tiedot</h3>
                          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1 text-[15px]">
                            {filled.map(({ def, value }) => (
                              <div key={def.id} className="flex items-baseline gap-2 border-b border-dotted border-slate-200 py-1">
                                <dt className="text-slate-500 min-w-[120px]">{def.label}</dt>
                                <dd className="text-slate-900 font-medium flex-1">{value}</dd>
                              </div>
                            ))}
                          </dl>
                        </section>
                      );
                    })()}

                    {/* Tehdyt toimenpiteet — oma osio, kevyt otsikko + vihreä ikoni */}
                    {(u.description || "").trim() && (
                      <section className="mt-10" data-testid={`public-unit-description-${u.id}`}>
                        <h3 className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2 inline-flex items-center gap-1.5">
                          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> Tehdyt toimenpiteet
                        </h3>
                        <div className="text-[15px] text-slate-800 leading-relaxed whitespace-pre-line">{u.description}</div>

                        {workChecklistBadges.length > 0 && (
                          <div className="mt-4 flex flex-wrap gap-1.5">
                            {workChecklistBadges.map((c) => (
                              <span
                                key={c.id}
                                className="badge inline-flex items-center gap-1 badge-emerald"
                              >
                                <CheckCircle2 className="h-3 w-3" />
                                {c.label}
                              </span>
                            ))}
                          </div>
                        )}
                      </section>
                    )}

                    {/* Työvaiheet erillisenä osiona, jos ei kuvausta mutta on ruksattuja työvaiheita */}
                    {!(u.description || "").trim() && workChecklistBadges.length > 0 && (
                      <section className="mt-10">
                        <h3 className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2 inline-flex items-center gap-1.5">
                          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> Tehdyt toimenpiteet
                        </h3>
                        <div className="flex flex-wrap gap-1.5">
                          {workChecklistBadges.map((c) => (
                            <span
                              key={c.id}
                              className="badge inline-flex items-center gap-1 badge-emerald"
                            >
                              <CheckCircle2 className="h-3 w-3" />
                              {c.label}
                            </span>
                          ))}
                        </div>
                      </section>
                    )}

                    {/* Kuvat — oma osio */}
                    {u.photos?.length > 0 && (
                      <section className="mt-10">
                        <h3 className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Kuvat</h3>
                        <div className="photo-grid grid grid-cols-1 md:grid-cols-2 print:grid-cols-2 gap-4 md:gap-5">
                          {u.photos.map((p, i) => (
                            <figure
                              key={p.photo_id}
                              className={`rounded-2xl overflow-hidden bg-white border border-slate-200 shadow-sm print-photo-card ${getPhotoSpanClass(i, u.photos.length)}`}
                            >
                              <button
                                type="button"
                                onClick={() => setLightbox({ photos: u.photos, startIndex: i })}
                                className="block w-full focus:outline-none focus:ring-2 focus:ring-emerald-400 print:cursor-default print:ring-0"
                                data-testid={`public-photo-${p.photo_id}`}
                              >
                                <img
                                  src={`${BACKEND}/api/photos/${p.photo_id}/thumb`}
                                  alt={p.caption || ""}
                                  loading="eager"
                                  decoding="sync"
                                  className="w-full aspect-[4/3] object-cover bg-slate-50 hover:opacity-95 transition-opacity"
                                />
                              </button>
                              {(p.caption || report.image_timestamps) && (
                                <figcaption className="px-4 py-2.5 text-sm text-slate-700 border-t border-slate-100 flex items-center justify-between gap-2">
                                  <span className="flex-1">{p.caption || ""}</span>
                                  {report.image_timestamps && p.created_at && (
                                    <span className="text-[11px] text-slate-400 whitespace-nowrap">
                                      {new Date(p.created_at).toLocaleString("fi-FI", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                                    </span>
                                  )}
                                </figcaption>
                              )}
                            </figure>
                          ))}
                        </div>
                      </section>
                    )}

                    {/* Tarvikkeet — leader-dot -tyyli */}
                    {!report.hide_materials && (u.materials_used || []).length > 0 && (
                      <section className="mt-10">
                        <h3 className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2 inline-flex items-center gap-1.5">
                          <Package className="h-3.5 w-3.5 text-blue-600" /> Tarvikkeet
                        </h3>
                        <dl className="space-y-0.5 text-[15px]">
                          {u.materials_used.map((m, i) => (
                            <div key={i} className="flex items-baseline gap-2">
                              <dt className="text-slate-800">{m.name}</dt>
                              <span className="flex-1 border-b border-dotted border-slate-300 translate-y-[-3px]" aria-hidden="true"></span>
                              <dd className="text-slate-700 whitespace-nowrap">{formatQty(m.qty)} {m.unit || "kpl"}</dd>
                            </div>
                          ))}
                        </dl>
                      </section>
                    )}

                    {/* Havainnot — valkoinen, vasen amber-reunakorostus, yhtenäinen lista */}
                    {!report.hide_findings_section && hasFindings && (
                      <section className="mt-10 rounded-lg border border-slate-200 border-l-[3px] border-l-amber-500 bg-white p-4 break-inside-avoid" data-testid={`public-unit-findings-${u.id}`}>
                        <h3 className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold inline-flex items-center gap-1.5 mb-2">
                          <AlertTriangle className="h-3.5 w-3.5 text-amber-600" /> Havainnot
                        </h3>
                        {(() => {
                          const extraLines = ((u.additional_work_text || "").trim() || "")
                            .split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
                          const bullets = [...findings, ...extraLines];
                          if (bullets.length === 0) return null;
                          return (
                            <ul className="text-[15px] text-slate-800 space-y-0.5">
                              {bullets.map((label, i) => (
                                <li key={i} className="flex items-baseline gap-2">
                                  <span className="text-slate-400 leading-none">•</span>
                                  <span className="flex-1">{label}</span>
                                </li>
                              ))}
                            </ul>
                          );
                        })()}
                      </section>
                    )}
                  </article>
                  );
                })}
              </div>
            </div>

            {report.request_signature && (report.signature_data || report.signature_name) && (
              <div className="break-inside-avoid" style={{ pageBreakInside: "avoid", breakInside: "avoid" }} data-testid="public-signature">
                <h2 className="font-display text-2xl font-semibold text-slate-900 mb-3">Allekirjoitus</h2>
                <div className="max-w-sm">
                  {report.signature_data ? (
                    <img
                      src={report.signature_data}
                      alt="Allekirjoitus"
                      className="h-24 w-full object-contain border-b-2 border-slate-300 bg-white"
                    />
                  ) : (
                    <div className="h-24 border-b-2 border-slate-300"></div>
                  )}
                  <div className="mt-2 text-sm text-slate-600">Työn suorittaja</div>
                  {report.signature_name && (
                    <div className="text-base text-slate-800 mt-0.5 font-medium">{report.signature_name}</div>
                  )}
                </div>
              </div>
            )}

            <div className="pt-6 border-t border-slate-100 text-center">
              <div className="text-xs text-slate-500">
                Powered by <a href="/" className="text-emerald-600 font-semibold hover:underline">Veraso Reports</a>
              </div>
            </div>
          </section>
        </div>
      </div>

      {lightbox && (
        <PublicPhotoLightbox
          photos={lightbox.photos}
          startIndex={lightbox.startIndex}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}
